import {useEffect} from "react";
import * as React from "react";

import {useMutation} from "@repo/data-gql";

import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";

import {CREATE_STRIPE_SESSION} from "./join-wizard/queries.ts";

interface Props {
    priceID: string;
    paymentFrequency: "ONE_TIME" | "MONTHLY" | "YEARLY";
    cancelURL: string;
    successURL: string;
    couponID?: string;
    renderLoading: () => React.ReactElement;
    renderError: () => React.ReactElement;
}

// Generates a unique Stripe Checkout session and redirects the browser
// to it.
//
// Why is this its own component? Why isn't this a hook?
//
// Khan Academy tracks checkout sessions for analytics and marketing
// purposes. For example, abandoned checkout sessions may trigger an
// email to the customer encouraging them to complete checkout.
//
// To avoid interfering with these downstream activities we don't want
// to generate checkout sessions unless we're sure the customer is
// going to be directed to checkout.
//
// Effects cannot be called conditionally in React. For example, we
// can't say "if this customer is eligible then redirect them to
// Stripe checkout".
//
// Instead, the operation is made into its own component which _can_ be
// rendered conditionally.
export default function StripeCheckoutRedirect(
    props: Props,
): React.ReactElement {
    const [createStripeSession, stripeSessionResult] = useMutation(
        CREATE_STRIPE_SESSION,
    );

    useEffect(() => {
        createStripeSession({
            priceID: props.priceID,
            paymentFrequency: props.paymentFrequency,
            cancelURL: props.cancelURL,
            successURL: props.successURL,
            couponID: props.couponID,
        });
    }, [
        createStripeSession,
        props.priceID,
        props.paymentFrequency,
        props.cancelURL,
        props.successURL,
        props.couponID,
    ]);

    if (!stripeSessionResult || stripeSessionResult.status === "loading") {
        return props.renderLoading();
    }

    if (
        stripeSessionResult.status !== "success" ||
        stripeSessionResult.data.createStripeSession?.error ||
        !stripeSessionResult.data.createStripeSession?.redirectURL
    ) {
        return props.renderError();
    }

    const sessionURL = stripeSessionResult.data.createStripeSession.redirectURL;

    return <SmartRedirect to={sessionURL} />;
}
